import React, { useState, useRef } from "react";
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import GuestHeader from "../GuestHeader";
import GuestFooter from "../GuestFooter";
import { insertVBSRegisterDetails } from "../GuestApiServices";
/* import { insertVBSRegisterDetails } from "../GuestApiServices"; */

const VBSRegistration = () => {
    // State variables for VBS registration form
    const [childFullName, setChildFullName] = useState('');
    const [childAge, setChildAge] = useState(null);
    const [childParentName, setChildParentName] = useState('');
    const [parentContactNumber, setParentContactNumber] = useState('');
    const [countryCode, setCountryCode] = useState('+44');
    const [parentStayWithChild, setParentStayWithChild] = useState(false);
    const [parentStayName, setParentStayName] = useState('');
    const [foodAllergies, setFoodAllergies] = useState(['']);
    const [hasFoodAllergy, setHasFoodAllergy] = useState(false);

    const toast = useRef(null);

    // Country codes for dropdown
    const countryCodes = [
        { label: 'UK (+44)', value: '+44' },
        { label: 'USA/Canada (+1)', value: '+1' },
        { label: 'India (+91)', value: '+91' },
        { label: 'Australia (+61)', value: '+61' },
        { label: 'Germany (+49)', value: '+49' },
        { label: 'France (+33)', value: '+33' },
        { label: 'Italy (+39)', value: '+39' },
        { label: 'Spain (+34)', value: '+34' },
        { label: 'UAE (+971)', value: '+971' },
        { label: 'Singapore (+65)', value: '+65' }
    ];

    // Allergy field handlers
    const addAllergyField = () => {
        setFoodAllergies([...foodAllergies, '']);
    };

    const removeAllergyField = (index) => {
        if (foodAllergies.length > 1) {
            const updatedAllergies = foodAllergies.filter((_, i) => i !== index);
            setFoodAllergies(updatedAllergies);
        }
    };

    const updateAllergy = (index, value) => {
        const updatedAllergies = [...foodAllergies];
        updatedAllergies[index] = value;
        setFoodAllergies(updatedAllergies);
    };

    // Validation Logic
    const validateForm = () => {
        // Required fields
        if (!childFullName?.trim() || !childAge || !childParentName?.trim() || !parentContactNumber?.trim()) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please fill in all required fields.',
                life: 3000
            });
            return false;
        }

        // Age validation
        const age = childAge;
        if (age < 3 || age > 16) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please enter a valid age between 3 and 16 years.',
                life: 3000
            });
            return false;
        }

        // Contact number validation
        const phoneRegex = /^[0-9\s\-\(\)]+$/;
        if (!phoneRegex.test(parentContactNumber) || parentContactNumber.replace(/\D/g, '').length < 6) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please enter a valid contact number.',
                life: 3000
            });
            return false;
        }

        // If parent is staying with child, name is required
        if (parentStayWithChild && !parentStayName.trim()) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please provide the name of the adult staying with the child.',
                life: 3000
            });
            return false;
        }

        // If child has food allergy, at least one allergy should be specified
        if (hasFoodAllergy) {
            const validAllergies = foodAllergies.filter(allergy => allergy.trim() !== '');
            if (validAllergies.length === 0) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Please specify at least one food allergy.',
                    life: 3000
                });
                return false;
            }
        }

        return true;
    };

    // Submission Handler
    const handleSubmit = (event) => {
        event.preventDefault();
        if (validateForm()) {
            // Filter out empty allergies
            const filteredAllergies = hasFoodAllergy 
                ? foodAllergies.filter(allergy => allergy.trim() !== '')
                : [];

            // Collect the form data for API submission
            const data = {
                childFullName: childFullName.trim(),
                childAge: childAge,
                childParentName: childParentName.trim(),
                parentContactNumber: `${countryCode} ${parentContactNumber}`,
                parentStayWithChild,
                parentStayName: parentStayWithChild ? parentStayName.trim() : '',
                hasFoodAllergy,
                foodAllergies: filteredAllergies,
                registrationDate: new Date().toISOString().split('T')[0]
            };

            console.log("VBS Registration data submitted:", data);
            
          insertVBSRegisterDetails(data).then((response) => {
                toast.current.show({
                    severity: 'success',
                    summary: response.data.message,
                    detail: 'Your child has been registered for VBS successfully!',
                    life: 4000
                });

                // Reset form
                setChildFullName('');
                setChildAge(null);
                setChildParentName('');
                setParentContactNumber('');
                setCountryCode('+44');
                setParentStayWithChild(false);
                setParentStayName('');
                setHasFoodAllergy(false);
                setFoodAllergies(['']);

            }).catch((error) => {
                console.error('Registration error:', error);
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Registration Failed', 
                    detail: 'Failed to submit registration. Please try again.' 
                });
            }); 
        }
    };

    const header = (
        <div className="text-center p-4">
            <h2 className="mb-2 text-2xl font-bold">Vacation Bible School Registration</h2>
            <p className="text-sm text-gray-600">Join us for a fun-filled spiritual learning experience</p>
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <GuestHeader />

            <div className="content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-8">
                            <Card header={header} className="shadow-2 mb-4">
                            </Card>

                            <Card className="shadow-2">
                                <form onSubmit={handleSubmit} id="vbsRegistrationForm">
                                    
                                    {/* Child Information Section */}
                                    <h3 className="mb-3 text-lg font-semibold border-bottom-1 surface-border pb-2">
                                        Child Information
                                    </h3>
                                    <div className="p-fluid mb-4">
                                        <div className="field mb-3">
                                            <label htmlFor="childFullName" className="font-medium block mb-2">
                                                Child's Full Name *
                                            </label>
                                            <InputText
                                                id="childFullName"
                                                value={childFullName}
                                                onChange={(e) => setChildFullName(e.target.value)}
                                                placeholder="Enter child's full name"
                                                required
                                                className="w-full"
                                            />
                                        </div>

                                        <div className="field mb-3">
                                            <label htmlFor="childAge" className="font-medium block mb-2">
                                                Child's Age *
                                            </label>
                                            <InputNumber
                                                id="childAge"
                                                value={childAge}
                                                onValueChange={(e) => setChildAge(e.value)}
                                                placeholder="Enter age"
                                                min={3}
                                                max={16}
                                                showButtons
                                                className="w-full"
                                                required
                                            />
                                            <small className="text-gray-600">Ages 3-16 years</small>
                                        </div>
                                    </div>

                                    {/* Parent/Guardian Information */}
                                    <h3 className="mb-3 text-lg font-semibold border-bottom-1 surface-border pb-2">
                                        Parent/Guardian Information
                                    </h3>
                                    <div className="p-fluid mb-4">
                                        <div className="field mb-3">
                                            <label htmlFor="childParentName" className="font-medium block mb-2">
                                                Parent/Guardian Name *
                                            </label>
                                            <InputText
                                                id="childParentName"
                                                value={childParentName}
                                                onChange={(e) => setChildParentName(e.target.value)}
                                                placeholder="Enter parent/guardian name"
                                                required
                                                className="w-full"
                                            />
                                        </div>

                                        <div className="field mb-3">
                                            <label htmlFor="parentContactNumber" className="font-medium block mb-2">
                                                Contact Number *
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="flex-2">
                                                    <Dropdown
                                                        value={countryCode}
                                                        onChange={(e) => setCountryCode(e.value)}
                                                        options={countryCodes}
                                                        placeholder="Code"
                                                        className="w-full"
                                                        disabled
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <InputText
                                                        id="parentContactNumber"
                                                        value={parentContactNumber}
                                                        onChange={(e) => setParentContactNumber(e.target.value)}
                                                        placeholder="Enter phone number"
                                                        required
                                                        className="w-full"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Parent Stay Information */}
                                    <h3 className="mb-3 text-lg font-semibold border-bottom-1 surface-border pb-2">
                                        Session Information
                                    </h3>
                                    <div className="mb-4">
                                        <div className="field-checkbox mb-3">
                                            <Checkbox
                                                inputId="parentStayWithChild"
                                                checked={parentStayWithChild}
                                                onChange={(e) => setParentStayWithChild(e.checked)}
                                            />
                                            <label htmlFor="parentStayWithChild" className="ml-2">
                                                Will a parent/guardian stay with the child during VBS sessions?
                                            </label>
                                        </div>
                                        
                                        {parentStayWithChild && (
                                            <div className="field mb-3 ml-4">
                                                <label htmlFor="parentStayName" className="font-medium block mb-2">
                                                    Name of Adult Staying with Child *
                                                </label>
                                                <InputText
                                                    id="parentStayName"
                                                    value={parentStayName}
                                                    onChange={(e) => setParentStayName(e.target.value)}
                                                    placeholder="Enter name of adult staying"
                                                    required={parentStayWithChild}
                                                    className="w-full"
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Food Allergy Information */}
                                    <h3 className="mb-3 text-lg font-semibold border-bottom-1 surface-border pb-2">
                                        Health & Safety Information
                                    </h3>
                                    <div className="mb-4">
                                        <div className="field-checkbox mb-3">
                                            <Checkbox
                                                inputId="hasFoodAllergy"
                                                checked={hasFoodAllergy}
                                                onChange={(e) => setHasFoodAllergy(e.checked)}
                                                className="text-success"
                                            />
                                            <label htmlFor="hasFoodAllergy" className="ml-2">
                                                Does the child have any food allergies or dietary restrictions?
                                            </label>
                                        </div>
                                        
                                        {hasFoodAllergy && (
                                            <div className="ml-4">
                                                <label className="font-medium block mb-2">
                                                    Please specify food allergies or dietary restrictions *
                                                </label>
                                                <div className="space-y-2 mb-3">
                                                    {foodAllergies.map((allergy, index) => (
                                                        <div key={index} className="flex gap-2 items-center">
                                                            <div className="flex-1">
                                                                <InputText
                                                                    value={allergy}
                                                                    onChange={(e) => updateAllergy(index, e.target.value)}
                                                                    placeholder={`Allergy or restriction ${index + 1}`}
                                                                    required={hasFoodAllergy && index === 0}
                                                                    className="w-full"
                                                                />
                                                            </div>
                                                            {foodAllergies.length > 1 && (
                                                                <Button
                                                                    type="button"
                                                                    icon="pi pi-times"
                                                                    className="p-button-danger p-button-outlined"
                                                                    onClick={() => removeAllergyField(index)}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <Button
                                                    type="button"
                                                    icon="pi pi-plus"
                                                    label="Add Another Allergy"
                                                    className="p-button-secondary p-button-outlined"
                                                    onClick={addAllergyField}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="text-center mt-4">
                                        <Button 
                                            type="submit" 
                                            label="Complete Registration"
                                            icon="pi pi-check"
                                            className="p-button-primary"
                                            size="large"
                                        />
                                        <p className="text-sm text-gray-500 mt-3">
                                            * Required fields must be filled
                                        </p>
                                    </div>

                                     <div className="p-message p-message-info mb-4">
                                        <div className="p-message-wrapper">
                                            <span className="p-message-icon pi pi-info-circle"></span>
                                            <span className="p-message-text">
                                                Please complete all required fields to ensure the safety and well-being of your child.
                                            </span>
                                        </div>
                                    </div>
                                </form>
                            </Card>

                            {/* Additional Information */}
                            <Card className="shadow-2 mt-4">
                                <h3 className="text-lg font-semibold mb-3">What to Expect</h3>
                                <div className="grid">
                                    <div className="col-12 md:col-4 text-center p-3">
                                        <i className="pi pi-palette text-3xl text-blue-500 mb-2"></i>
                                        <p className="text-sm">Creative Bible Lessons & Activities</p>
                                    </div>
                                    <div className="col-12 md:col-4 text-center p-3">
                                        <i className="pi pi-volume-up text-3xl text-green-500 mb-2"></i>
                                        <p className="text-sm">Fun Music & Worship Sessions</p>
                                    </div>
                                    <div className="col-12 md:col-4 text-center p-3">
                                        <i className="pi pi-users text-3xl text-orange-500 mb-2"></i>
                                        <p className="text-sm">New Friends & Community Building</p>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            <GuestFooter />
        </>
    );
}

export default VBSRegistration;